/**
 * Firebase Web SDK initialization, driven entirely by VITE_* env vars.
 * NO secrets here — the Firebase WEB api key is public and safe in the client.
 * Provider API keys and the SA key live ONLY in Cloudflare Worker secrets.
 *
 * When VITE_USE_EMULATORS is "true", connects Auth + Firestore SDKs to the
 * local emulators so the same code path runs in dev/CI without real cloud creds.
 */

import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  connectAuthEmulator,
  setPersistence,
  browserLocalPersistence,
  type Auth,
} from "firebase/auth";
import {
  getFirestore,
  connectFirestoreEmulator,
  type Firestore,
} from "firebase/firestore";

export interface FirebaseEnv {
  apiKey: string;
  authDomain: string;
  projectId: string;
  appId?: string;
  /** Worker API base URL, e.g. https://autodieta-worker.<sub>.workers.dev */
  workerUrl: string;
  useEmulators: boolean;
}

/** Read and validate the Firebase config from import.meta.env (VITE_*). */
export function readFirebaseEnv(
  env: Record<string, string | boolean | undefined> = import.meta.env,
): FirebaseEnv {
  const apiKey = String(env["VITE_FIREBASE_API_KEY"] ?? "");
  const authDomain = String(env["VITE_FIREBASE_AUTH_DOMAIN"] ?? "");
  const projectId = String(env["VITE_FIREBASE_PROJECT_ID"] ?? "");
  const appId = env["VITE_FIREBASE_APP_ID"] ? String(env["VITE_FIREBASE_APP_ID"]) : undefined;
  const workerUrl = String(env["VITE_WORKER_URL"] ?? "");
  const useEmulators = String(env["VITE_USE_EMULATORS"] ?? "") === "true";

  if (!apiKey || !authDomain || !projectId) {
    throw new Error(
      "Missing Firebase config. Set VITE_FIREBASE_API_KEY, VITE_FIREBASE_AUTH_DOMAIN, VITE_FIREBASE_PROJECT_ID.",
    );
  }
  if (!workerUrl) {
    console.warn("[firebase] VITE_WORKER_URL is not set. Worker features will be unavailable.");
  }
  return {
    apiKey,
    authDomain,
    projectId,
    ...(appId ? { appId } : {}),
    workerUrl,
    useEmulators,
  };
}

export interface FirebaseContext {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  env: FirebaseEnv;
}

let cached: FirebaseContext | undefined;

/**
 * Initialize Firebase once (idempotent). Connects to emulators when configured.
 * Emulator hosts match firebase.json defaults (auth :9099, firestore :8080).
 */
export function initFirebase(env: FirebaseEnv = readFirebaseEnv()): FirebaseContext {
  if (cached) return cached;

  const app = initializeApp({
    apiKey: env.apiKey,
    authDomain: env.authDomain,
    projectId: env.projectId,
    ...(env.appId ? { appId: env.appId } : {}),
  });
  const auth = getAuth(app);
  const db = getFirestore(app);

  if (env.useEmulators) {
    // disableWarnings keeps the console quiet about the insecure local key.
    connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
    connectFirestoreEmulator(db, "127.0.0.1", 8080);
  } else {
    // Pin local persistence explicitly so a long-open or reopened tab stays
    // signed in instead of relying on the SDK default.
    void setPersistence(auth, browserLocalPersistence).catch(() => {});
  }

  cached = { app, auth, db, env };
  return cached;
}

/** Test-only: reset the memoized context so a fresh init can run. */
export function resetFirebaseForTests(): void {
  cached = undefined;
}
